// React imports
import React, { useState, useEffect, useMemo } from 'react';

// Generic Imports
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import Autosuggest from 'react-autosuggest';
import CurrencyList from 'currency-list';
import _ from 'lodash';

// MUI Imports
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Button from '@mui/material/Button';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';

function CurrencyDialog(props) {
    const { open, onClose, initialCurrency, onCurrency, defaultCurrency, extraCurrencies = 'USD,EUR,CNY' } = props;
    const currencies = useMemo(() => _.values(CurrencyList.getAll('en_US')), []);
    const [ value, setValue ] = useState('');
    const [ error, setError ] = useState(false);
    const [ suggestions, setSuggestions ] = useState(currencies);
    const [ picked, setPicked ] = useState(null);

    function onSuggestionsFetchRequested({ value }) { setSuggestions(currencies.filter(c => suggestionToText(c).toUpperCase().indexOf(value.toUpperCase()) > -1)); }
    function onSuggestionsClearRequested() { setSuggestions([]); }

    function onOK() {
        if (!picked) {
            setError(true);
            return;
        }

        onCurrency(picked);
    }

    useEffect(() => {
        if (!initialCurrency) {
            setValue(''); setPicked(null);
            return;
        }
        setValue(suggestionToText(CurrencyList.get(initialCurrency)));
        setPicked(initialCurrency);
    }, [initialCurrency]);

    const ec = _.isEmpty((extraCurrencies || '').split(',')) ? ['USD', 'EUR', 'CNY'] : extraCurrencies.split(',');

    return (
        <Dialog open={open} onClose={onClose} maxWidth={'sm'} fullWidth PaperProps={{ sx: { overflowY: 'visible' } }} sx={{ zIndex: 1000050 }}>
            <DialogTitle>
                Currency Selector
            </DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ paddingBottom: '12px' }}>
                    Select a currency from the list below:
                </DialogContentText>
                <Autosuggest
                    suggestions={suggestions}
                    onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                    onSuggestionsClearRequested={onSuggestionsClearRequested}
                    getSuggestionValue={(suggestion) => {
                        setError(false);
                        setValue(suggestionToText(suggestion));
                        setPicked(suggestion.code);
                        return suggestionToText(suggestion);
                    }}
                    renderSuggestion={renderSuggestion}
                    renderInputComponent={renderInputComponent}
                    renderSuggestionsContainer={renderSuggestionsContainer}
                    inputProps={{
                        value,
                        error,
                        onFocus: e => {
                            e.target.select();
                        },
                        onChange: (e, { newValue }) => {
                            if (e.type === 'change') {
                                setError(false);
                                setPicked(null);
                                setValue(newValue || '')
                            }
                        },
                        autoFocus: true,
                    }}
                />
                <DialogContentText sx={{ padding: '12px 0px' }}>
                    Or pick from one of the popular currencies below:
                </DialogContentText>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    {_.uniq(_.compact([ defaultCurrency, ...ec ])).map(c => (
                        <Button key={c} variant='outlined' color='primary' disabled={picked === c} sx={{ width: '150px', height: '57px', textTransform: 'none' }} onClick={() => onCurrency(c)}>
                            {c} ({CurrencyList.get(c).symbol})
                        </Button>
                    ))}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button variant='outlined' color='secondary' onClick={onClose}>
                    Cancel
                </Button>
                <Button variant='contained' color='primary' onClick={onOK}>
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default CurrencyDialog;

function renderInputComponent(inputProps) {
    const {
        classes, inputRef = () => {}, ref, ...other
    } = inputProps;

    return (
        <OutlinedInput fullWidth inputprops={{ inputRef: (node) => { ref(node); inputRef(node); }, }} {...other} />
    );
}

function renderSuggestionsContainer(options) {
    if (!options.children) return null;
    return (
        <Paper {...options.containerProps} classes={{ root: 'registration-no-ul' }} sx={{ position: 'absolute !important', width: 'calc(100% - 50px)', padding: '12px' }} square>
            {options.children}
        </Paper>
    );
}

function renderSuggestion(suggestion, { query, isHighlighted }) {
    const text = suggestionToText(suggestion);
    const matches = match(text, query, { insideWords: true });
    const parts = parse(text, matches);

    return (
        <MenuItem selected={isHighlighted} component='div'>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {parts.map((part, index) => (part.highlight ? (
                    <span key={String(index)} style={{ color: '#000', fontWeight: '500' }}>
                        {part.text}
                    </span>
                ) : (
                    <span key={String(index)} style={{ color: '#666' }}>
                        {part.text}
                    </span>
                )))}
            </div>
        </MenuItem>
    );
}

function suggestionToText(c) {
    if (!c) return '';
    return `${c.code} (${c.symbol}) - ${c.name}`; 
}
